import {
  DarwinFlex,
  DMenuProps,
  DTableProps,
  StatusChip,
  StatusChipColors,
  renderUserCell,
  renderDateCell,
  ActionsDropdown,
  CombinedText,
  STATUS_COL_WIDTH,
  ACTIONS_COL_WIDTH,
} from '@darwin-ui';
import Icon from 'components/darwin-ui/Icon';
import { TFunction } from 'i18next';
import { ColumnAction } from '.';
import { UserRecord } from 'types/userTypes';

export const getUsersColumns = (
  t: TFunction,
  actions: ColumnAction[],
  searchQuery: string
) => {
  const menuItems = (user: UserRecord): DMenuProps['items'] =>
    actions.map(action => ({
      key: action.key,
      label:
        action.key === 'activate-deactivate' ? (
          <div onClick={() => action?.onClick?.(user)}>
            <StatusChip
              label={
                user?.active
                  ? t('users-page.users-list.deactivate')
                  : t('users-page.users-list.activate')
              }
              color={
                user?.active ? StatusChipColors.red : StatusChipColors.green
              }
            />
          </div>
        ) : (
          <DarwinFlex
            gap={8}
            align="center"
            onClick={() => action?.onClick?.(user)}
          >
            <Icon
              name={action.name ? action.name : 'darwin-user'}
              size="sm"
              color="inherit"
            />
            {action.label}
          </DarwinFlex>
        ),
    }));

  const renderUserColumn = (userObj: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  }) => {
    const fullName =
      userObj && (userObj.firstName || userObj.lastName)
        ? `${userObj.firstName || ''} ${userObj.lastName || ''}`.trim()
        : '';
    return renderUserCell(fullName, searchQuery);
  };

  const columns: DTableProps<UserRecord>['columns'] = [
    {
      title: t('users-page.users-list.columns.first-name'),
      dataIndex: ['user', 'firstName'],
      key: 'firstName',
      sorter: true,
      render: (_, record) => (
        <CombinedText
          lines={1}
          text={record.user.firstName}
          highlight={searchQuery}
        />
      ),
      width: 100,
    },
    {
      title: t('users-page.users-list.columns.last-name'),
      dataIndex: ['user', 'lastName'],
      key: 'lastName',
      sorter: true,
      render: (_, record) => (
        <CombinedText
          lines={1}
          text={record.user.lastName}
          highlight={searchQuery}
        />
      ),
      width: 100,
    },
    {
      title: t('users-page.users-list.columns.organization'),
      dataIndex: ['company', 'name'],
      key: 'company',
      sorter: true,
      render: (_, record) => (
        <CombinedText
          lines={1}
          text={record.company.name}
          highlight={searchQuery}
        />
      ),
      width: 220,
    },
    {
      title: t('users-page.users-list.columns.email'),
      dataIndex: ['user', 'email'],
      key: 'email',
      sorter: true,
      render: (_, record) => (
        <CombinedText
          lines={1}
          text={record.user.email}
          highlight={searchQuery}
        />
      ),
    },
    {
      title: t('users-page.users-list.columns.role'),
      dataIndex: ['role', 'name'],
      key: 'role',
      sorter: true,
      render: (_, record) => (
        <CombinedText
          lines={1}
          text={record.role.name}
          highlight={searchQuery}
        />
      ),
      width: 100,
    },
    {
      title: t('users-page.users-list.columns.status'),
      dataIndex: ['user', 'active'],
      key: 'status',
      sorter: true,
      render: (_, record) => (
        <StatusChip
          color={record?.active ? StatusChipColors.green : StatusChipColors.red}
          label={
            record?.active
              ? t('users-page.users-list.columns.active')
              : t('users-page.users-list.columns.inactive')
          }
        />
      ),
      width: STATUS_COL_WIDTH,
    },
    {
      title: t('users-page.users-list.columns.job-title'),
      dataIndex: 'jobTitle',
      key: 'jobTitle',
      sorter: true,
      width: 140,
      render: (_, record) => (
        <CombinedText
          lines={1}
          text={record.jobTitle || ''}
          highlight={searchQuery}
        />
      ),
    },
    {
      title: t('users-page.users-list.columns.phone-number'),
      dataIndex: ['user', 'phoneCode', 'phoneNumber'],
      key: 'phoneNumber',
      sorter: true,
      render: (_, record) => (
        <CombinedText
          lines={1}
          text={`${record.user.phoneCode || ''} ${record.user.phoneNumber}`}
          highlight={searchQuery}
        />
      ),
    },
    {
      title: t('users-page.users-list.columns.language'),
      dataIndex: ['user', 'language'],
      key: 'language',
      sorter: true,
      width: 100,
      render: (_, record) => (
        <CombinedText
          lines={1}
          text={record.user.language || ''}
          highlight={searchQuery}
        />
      ),
    },
    {
      title: t('users-page.users-list.columns.time-zone'),
      dataIndex: ['user', 'timeZone'],
      key: 'timeZone',
      sorter: true,
      width: 140,
      render: (_, record) => (
        <CombinedText
          lines={1}
          text={record.user.timeZone || ''}
          highlight={searchQuery}
        />
      ),
    },
    {
      title: t('users-page.users-list.columns.created-by'),
      dataIndex: ['user', 'createdBy'],
      key: 'createdBy',
      sorter: true,
      render: renderUserColumn,
    },
    {
      title: t('users-page.users-list.columns.created-date'),
      dataIndex: ['user', 'createdAt'],
      key: 'createdAt',
      sorter: true,
      width: 160,
      render: (_, record) => (
        <CombinedText
          lines={1}
          text={renderDateCell(record.user.createdAt)}
          highlight={searchQuery}
        />
      ),
    },
    {
      title: t('users-page.users-list.columns.modified-by'),
      dataIndex: ['user', 'updatedBy'],
      key: 'updatedBy',
      sorter: true,
      render: renderUserColumn,
    },
    {
      title: t('users-page.users-list.columns.modified-date'),
      dataIndex: ['user', 'updatedAt'],
      key: 'updatedAt',
      sorter: true,
      render: (_, record) => (
        <CombinedText
          lines={1}
          text={renderDateCell(record.user.updatedAt)}
          highlight={searchQuery}
        />
      ),
    },
    {
      dataIndex: 'actions',
      key: 'actions',
      fixed: 'right',
      align: 'center',
      render: (_: any, user: UserRecord) => (
        <ActionsDropdown item={user} getMenuItems={menuItems} />
      ),
      width: ACTIONS_COL_WIDTH,
    },
  ];

  return columns;
};
